﻿import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { Loader2 } from 'lucide-react';
import { format, addMonths, addDays } from 'date-fns';
import type { ApiError } from '@pms/shared';
import { subscriptionService, type SubscriptionDetail } from '@/services/subscription.service';
import { departmentsService } from '@/services/departments.service';
import { usersService } from '@/services/users.service';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

const NONE = '__none__';

interface FormValues {
  name: string;
  vendor_name: string;
  category_id: string;
  department_id: string;
  billing_cycle_id: string;
  custom_interval_days: string;
  assigned_to: string;
  amount: string;
  currency_id: string;
  status_id: string;
  end_date: string;
  reminder_days: string;
  auto_renewal: boolean;
  notes: string;
}

function computeNextDue(startDate: string, cycleCode: string, intervalMonths: number | null, intervalDays: number | null, customDays: string): string | null {
  if (!startDate || !cycleCode) return null;
  const base = new Date(startDate);
  if (cycleCode === 'one_time') return null;
  if (cycleCode === 'custom') {
    const d = parseInt(customDays, 10);
    if (!d) return null;
    return format(addDays(base, d), 'dd MMM yyyy');
  }
  if (intervalMonths) return format(addMonths(base, intervalMonths), 'dd MMM yyyy');
  if (intervalDays) return format(addDays(base, intervalDays), 'dd MMM yyyy');
  return null;
}

interface Props {
  open: boolean;
  subscription: SubscriptionDetail | null;
  onClose: () => void;
}

export function EditSubscriptionModal({ open, subscription, onClose }: Props) {
  const queryClient = useQueryClient();

  const { data: categories = [] } = useQuery({ queryKey: ['subscriptions', 'categories'], queryFn: () => subscriptionService.listCategories() });
  const { data: currencies = [] } = useQuery({ queryKey: ['subscriptions', 'currencies'], queryFn: () => subscriptionService.listCurrencies() });
  const { data: statuses = [] } = useQuery({ queryKey: ['subscriptions', 'statuses'], queryFn: () => subscriptionService.listStatuses() });
  const { data: billingCycles = [] } = useQuery({ queryKey: ['subscriptions', 'billing-cycles'], queryFn: () => subscriptionService.listBillingCycles() });
  const { data: deptsData } = useQuery({ queryKey: ['departments'], queryFn: () => departmentsService.list({ limit: 100 }) });
  const { data: usersData } = useQuery({ queryKey: ['users', 'basic'], queryFn: () => usersService.list({ limit: 100 }) });

  const { register, handleSubmit, reset, watch, setValue, control, formState: { errors, isSubmitting } } = useForm<FormValues>({
    defaultValues: { name: '', vendor_name: '', category_id: NONE, department_id: NONE, billing_cycle_id: '', custom_interval_days: '', assigned_to: NONE, amount: '', currency_id: '', status_id: '', end_date: '', reminder_days: '7', auto_renewal: false, notes: '' },
  });

  useEffect(() => {
    if (open && subscription) {
      reset({
        name: subscription.name,
        vendor_name: subscription.vendor_name ?? '',
        category_id: subscription.category_id ?? NONE,
        department_id: subscription.department_id ?? NONE,
        billing_cycle_id: subscription.billing_cycle_id,
        custom_interval_days: subscription.custom_interval_days != null ? String(subscription.custom_interval_days) : '',
        assigned_to: subscription.assigned_to_id ?? NONE,
        amount: String(subscription.amount),
        currency_id: subscription.currency_id,
        status_id: subscription.status_id,
        end_date: subscription.end_date ? subscription.end_date.substring(0, 10) : '',
        reminder_days: String(subscription.reminder_days),
        auto_renewal: subscription.auto_renewal,
        notes: subscription.notes ?? '',
      });
    }
  }, [open, subscription, reset]);

  const watchBillingCycleId = watch('billing_cycle_id');
  const watchCustomDays = watch('custom_interval_days');
  const selectedCycle = billingCycles.find((c) => c.id === watchBillingCycleId);
  const nextDuePreview = selectedCycle && subscription
    ? computeNextDue(subscription.start_date, selectedCycle.code, selectedCycle.interval_months, selectedCycle.interval_days, watchCustomDays)
    : null;

  const mutation = useMutation({
    mutationFn: (v: FormValues) =>
      subscriptionService.update(subscription!.id, {
        name: v.name.trim(),
        vendor_name: v.vendor_name.trim() || undefined,
        category_id: v.category_id === NONE ? undefined : v.category_id || undefined,
        department_id: v.department_id === NONE ? undefined : v.department_id || undefined,
        billing_cycle_id: v.billing_cycle_id,
        custom_interval_days: v.custom_interval_days ? parseInt(v.custom_interval_days, 10) : undefined,
        assigned_to: v.assigned_to === NONE ? undefined : v.assigned_to || undefined,
        amount: parseFloat(v.amount),
        currency_id: v.currency_id,
        status_id: v.status_id,
        end_date: v.end_date || undefined,
        reminder_days: parseInt(v.reminder_days, 10),
        auto_renewal: v.auto_renewal,
        notes: v.notes.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success('Subscription updated');
      void queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      onClose();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-full max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Edit Subscription</DialogTitle>
        </DialogHeader>
        {subscription && (
          <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
            {/* Start date — read only */}
            <div className="rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm">
              <span className="text-muted-foreground">Start date (read-only): </span>
              <span className="font-medium">{subscription.start_date ? format(new Date(subscription.start_date), 'dd MMM yyyy') : '—'}</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="edit-name">Subscription Name *</Label>
                <Input id="edit-name" {...register('name', { required: 'Name is required' })} />
                {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="edit-vendor">Vendor / Provider</Label>
                <Input id="edit-vendor" {...register('vendor_name')} />
              </div>

              <div className="space-y-1.5">
                <Label>Category</Label>
                <Controller control={control} name="category_id" render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>No category</SelectItem>
                      {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )} />
              </div>

              <div className="space-y-1.5">
                <Label>Department</Label>
                <Controller control={control} name="department_id" render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger><SelectValue placeholder="Select department" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>No department</SelectItem>
                      {(deptsData?.data ?? []).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )} />
              </div>

              <div className="space-y-1.5">
                <Label>Billing Cycle *</Label>
                <Controller control={control} name="billing_cycle_id" rules={{ required: 'Required' }} render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger><SelectValue placeholder="Select cycle" /></SelectTrigger>
                    <SelectContent>
                      {billingCycles.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )} />
              </div>

              {selectedCycle?.code === 'custom' && (
                <div className="space-y-1.5">
                  <Label htmlFor="edit-custom-days">Interval (days)</Label>
                  <Input id="edit-custom-days" type="number" min={1} {...register('custom_interval_days')} />
                </div>
              )}

              <div className="space-y-1.5">
                <Label>Assigned To</Label>
                <Controller control={control} name="assigned_to" render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger><SelectValue placeholder="Select user" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Unassigned</SelectItem>
                      {(usersData?.data ?? []).map((u) => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )} />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="edit-amount">Amount *</Label>
                <Input id="edit-amount" type="number" step="0.01" min={0.01} {...register('amount', { required: 'Required', validate: (v) => parseFloat(v) > 0 || 'Must be positive' })} />
                {errors.amount && <p className="text-xs text-destructive">{errors.amount.message}</p>}
              </div>

              <div className="space-y-1.5">
                <Label>Currency *</Label>
                <Controller control={control} name="currency_id" rules={{ required: 'Required' }} render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger><SelectValue placeholder="Select currency" /></SelectTrigger>
                    <SelectContent>
                      {currencies.map((c) => <SelectItem key={c.id} value={c.id}>{c.symbol} {c.code}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )} />
              </div>

              {nextDuePreview && (
                <div className="sm:col-span-2 rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm">
                  <span className="text-muted-foreground">Next due date will be: </span>
                  <span className="font-medium text-emerald-700">{nextDuePreview}</span>
                </div>
              )}

              <div className="space-y-1.5">
                <Label>Status *</Label>
                <Controller control={control} name="status_id" rules={{ required: 'Required' }} render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                    <SelectContent>
                      {statuses.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          <span className="flex items-center gap-2">
                            <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                            {s.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )} />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="edit-end">End Date</Label>
                <Input id="edit-end" type="date" {...register('end_date')} />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="edit-reminder">Reminder Days</Label>
                <Input id="edit-reminder" type="number" min={0} {...register('reminder_days')} />
              </div>

              <div className="flex items-center gap-2 pt-1">
                <Checkbox
                  id="edit-auto"
                  checked={watch('auto_renewal')}
                  onChange={(e) => setValue('auto_renewal', e.target.checked)}
                />
                <Label htmlFor="edit-auto" className="cursor-pointer font-normal">Enable auto-renewal</Label>
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="edit-notes">Notes</Label>
                <Textarea id="edit-notes" {...register('notes')} rows={2} />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={mutation.isPending || isSubmitting}>
                {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
